/**
 * Settings → Tools (slice 2a part 2 §3): each of the crew's tools, what it
 * does, and whether it works now, from `GET /api/tools`; and "Your apps",
 * what's connected on Integrations (slice 3b spec §5.1).
 */
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";
import {
  AppWindow,
  FileText,
  LayoutGrid,
  ListTodo,
  Paperclip,
  ScrollText,
  Search,
  SquareTerminal,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import type { ComputerStatus, Me } from "../../app/me";
import type { Tool, ToolId } from "./api";
import { useTools } from "./api";
import { useIntegrations } from "./integrations-api";
import { yourAppsLine } from "./integrations-view";
import { stateWords, TOOL_WORDS } from "./settings-view";

const ICONS: Record<ToolId, LucideIcon> = {
  search_web: Search,
  read_page: FileText,
  browser: AppWindow,
  files: SquareTerminal,
  tasks: ListTodo,
  docs: ScrollText,
  crew_files: Paperclip,
};

const ORDER: Record<Tool["group"], ToolId[]> = {
  web: ["search_web", "read_page"],
  computer: ["browser"],
  crew: ["files", "tasks", "docs", "crew_files"],
};

const TONE = { moss: "text-moss", copper: "text-copper", red: "text-destructive" };
const DOT = { moss: "bg-moss", copper: "bg-copper", red: "bg-destructive" };

/**
 * The "On its computer" card's footer (slice 6c §7.5, boards 94, 94b): a
 * link to the Computer page and the computer's state. None on a host with
 * no desktop, or for a computer in `error`.
 */
function computerFooter(computer: Me["computer"]): ReactNode {
  if (!computer?.desktop || computer.status === "error") return undefined;
  return (
    <>
      <span className="flex flex-col gap-[3px]">
        <span className="text-copy">Sign in to a site once, and the crew can use it after.</span>
        <span data-computer-state className="text-hint">
          {computerStateLine(computer.status)}
        </span>
      </span>
      <Link href="/computer" className="py-2 font-medium text-foreground underline">
        Open the computer →
      </Link>
    </>
  );
}

const ASLEEP = "The computer is asleep · wakes in about a minute";
const STATE_LINES: Partial<Record<ComputerStatus, string>> = {
  ready: "Awake",
  asleep: ASLEEP,
  sleeping: ASLEEP,
  waking: "Waking up",
};

const computerStateLine = (status: ComputerStatus) => STATE_LINES[status] ?? "Setting up";

export function ToolsTab({ owner, computer }: { owner: boolean; computer: Me["computer"] }) {
  const tools = useTools();
  // Only the latest read's states: none while one is in flight or after one
  // failed, never the last read's (spec §3.2, Devin on #212).
  const fresh = !tools.isError && !tools.isFetching;
  const byId = new Map(fresh ? tools.data?.tools.map((t) => [t.id, t]) : []);
  const limit = tools.data?.web.limit;
  return (
    <div className="flex flex-col gap-8">
      <ToolGroup
        title="The web"
        line="Crew's own tools, whichever AI you connected."
        ids={ORDER.web}
        byId={byId}
        failed={tools.isError}
        footer={
          <>
            <span>
              {limit ? `${limit} lookups a day, included in your plan.` : "Included in your plan."}
            </span>
            {owner ? (
              <Link href="/settings/usage" className="py-2 font-medium text-foreground underline">
                See usage →
              </Link>
            ) : null}
          </>
        }
      />
      <ToolGroup
        title="On its computer"
        line="Part of your crew's own computer."
        ids={ORDER.computer}
        byId={byId}
        failed={tools.isError}
        footer={computerFooter(computer)}
      />
      <ToolGroup
        title="Crew"
        line="How the crew keeps track of its work."
        ids={ORDER.crew}
        byId={byId}
        failed={tools.isError}
      />
      <YourApps />
      <p className="text-sm text-muted-foreground">
        Connect and manage your own apps in Integrations.
      </p>
    </div>
  );
}

function ToolGroup({
  title,
  line,
  ids,
  byId,
  failed,
  footer,
}: {
  title: string;
  line: string;
  ids: ToolId[];
  byId: Map<ToolId, Tool>;
  failed: boolean;
  footer?: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{line}</p>
      </div>
      <div className="overflow-hidden rounded-xl bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.08)]">
        <ul className="divide-y divide-border">
          {ids.map((id) => (
            <ToolRow key={id} id={id} tool={byId.get(id)} />
          ))}
        </ul>
        {footer ? (
          <div className="flex flex-col gap-1.5 border-t border-border bg-background/40 px-4 py-3 text-[13px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between md:px-5">
            {footer}
          </div>
        ) : null}
      </div>
      {failed ? <p className="text-sm text-muted-foreground">Couldn't check right now.</p> : null}
    </section>
  );
}

/** The fifth row (Paper 44): how many accounts and servers are connected, and a link there. */
function YourApps() {
  const list = useIntegrations();
  return (
    <div
      data-your-apps
      className="flex items-center gap-3.5 rounded-xl bg-card px-4 py-4 shadow-[0_0_0_1px_rgb(31_42_68/0.08)] md:px-5"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary">
        <LayoutGrid className="size-4 text-foreground" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-semibold text-foreground">Your apps</span>
        {list.data ? (
          <span className="text-[13px] text-muted-foreground">{yourAppsLine(list.data)}</span>
        ) : null}
      </div>
      <Link
        href="/settings/integrations"
        className="shrink-0 text-[13px] font-medium text-foreground underline"
      >
        Integrations →
      </Link>
    </div>
  );
}

function ToolRow({ id, tool }: { id: ToolId; tool: Tool | undefined }) {
  const Icon = ICONS[id];
  const words = TOOL_WORDS[id];
  return (
    <li
      data-tool={id}
      data-state={tool?.state}
      className="flex items-start gap-3.5 px-4 py-4 md:px-5"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary">
        <Icon className="size-4 text-foreground" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 md:flex-row md:items-start md:gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[15px] font-medium text-foreground">{words.name}</span>
          <span className="text-sm text-muted-foreground">{words.line}</span>
          {tool?.note ? <ToolNote note={tool.note} /> : null}
        </div>
        {tool ? <StateLabel state={tool.state} /> : null}
      </div>
    </li>
  );
}

/** The owner's reason a tool isn't working; "Settings → AI" links there. */
function ToolNote({ note }: { note: string }) {
  const [before, after] = note.split("Settings → AI");
  return (
    <span className="mt-1 text-sm text-destructive">
      {before}
      {after !== undefined ? (
        <>
          <Link href="/settings#ai" className="underline">
            Settings → AI
          </Link>
          {after}
        </>
      ) : null}
    </span>
  );
}

function StateLabel({ state }: { state: Tool["state"] }) {
  const { text, tone } = stateWords(state);
  return (
    <span
      className={cn("mt-1.5 flex shrink-0 items-center gap-1.5 text-[13px] md:mt-0.5", TONE[tone])}
    >
      <span className={cn("size-1.5 rounded-full", DOT[tone])} />
      {text}
    </span>
  );
}
